import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import {
  CreditCard, UsersThree, ArrowClockwise, Lightning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../lib/api';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

export default function MyCardsPage() {
  const [cards, setCards] = useState([]);
  const [subStaff, setSubStaff] = useState([]);
  const [autoOn, setAutoOn] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [targetSubId, setTargetSubId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/card-stock/my-cards');
      setCards(r.data.cards || []);
      setSubStaff(r.data.sub_staff || []);
      setAutoOn(!!r.data.auto_distribute_enabled);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected(s =>
    s.size === cards.length ? new Set() : new Set(cards.map(c => c.id))
  );

  const toggleAuto = async (val) => {
    try {
      await api.post('/card-stock/auto-distribute', { enabled: val });
      setAutoOn(val);
      toast.success(val ? 'Auto-distribute ON' : 'Auto-distribute OFF');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const distribute = async () => {
    if (!targetSubId) { toast.error('Sub-staff select karo'); return; }
    if (selected.size === 0) { toast.error('Cards select karo'); return; }
    setBusy(true);
    try {
      const r = await api.post('/card-stock/distribute-to-substaff', {
        card_ids: Array.from(selected),
        sub_staff_id: targetSubId,
      });
      toast.success(r.data.message);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Distribute failed');
    } finally { setBusy(false); }
  };

  const pullback = async (cardId, subId) => {
    if (!window.confirm('Card wapas apne paas le lein?')) return;
    try {
      await api.post('/card-stock/pullback-from-substaff', {
        card_ids: [cardId], sub_staff_id: subId,
      });
      toast.success('Pulled back');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Compute per-sub-staff allocation from cards list
  const perSubStaff = subStaff.map(s => {
    const cs = cards.filter(c => c.assigned_to_id === s.id);
    const bal = cs.reduce((t, c) => t + Number(c.available_limit || 0), 0);
    const used = cs.reduce((t, c) => t + Number(c.used_amount || 0), 0);
    return { ...s, count: cs.length, balance: bal, used };
  });
  const myCards = cards.filter(c => !c.assigned_to_id || subStaff.every(s => s.id !== c.assigned_to_id));

  return (
    <div className="space-y-5" data-testid="my-cards-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CreditCard size={26} weight="duotone" className="text-violet-600" /> My Cards
        </h1>
        <span className="text-sm text-gray-500">Apne paas ke cards + sub-staff distribution + tracking</span>
        <div className="ml-auto flex gap-2 items-center">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none bg-violet-50 border border-violet-300 rounded px-3 py-1.5" data-testid="auto-distribute-toggle">
            <Lightning size={14} weight={autoOn ? 'fill' : 'regular'} className={autoOn ? 'text-violet-700' : 'text-gray-400'} />
            <span className="text-xs font-bold">Auto-Distribute</span>
            <input type="checkbox" checked={autoOn} onChange={(e) => toggleAuto(e.target.checked)} className="accent-violet-600" />
            <span className={`text-[10px] font-bold ${autoOn ? 'text-emerald-600' : 'text-gray-400'}`}>{autoOn ? 'ON' : 'OFF'}</span>
          </label>
          <Button variant="outline" size="sm" onClick={load}>
            <ArrowClockwise size={14} /> Refresh
          </Button>
        </div>
      </div>

      {/* Auto-distribute help */}
      <div className="text-[11px] text-gray-600 bg-amber-50 border border-amber-200 rounded p-2">
        <b>Auto-Distribute ON:</b> Admin aapko naye cards assign karte hi, wo apne-aap round-robin se aapke sub-staff me baant diye jayenge.
        <b> OFF:</b> Aap manually distribute karein.
      </div>

      {/* Sub-staff allocation summary */}
      {perSubStaff.length > 0 && (
        <div className="bg-white border rounded-lg p-3">
          <div className="text-sm font-bold mb-2 flex items-center gap-1">
            <UsersThree size={16} /> Sub-Staff Allocation & Usage ({perSubStaff.length})
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {perSubStaff.map(s => (
              <div key={s.id} className="bg-blue-50 border border-blue-200 rounded p-2 text-sm" data-testid={`sub-staff-card-${s.id}`}>
                <div className="font-semibold">{s.name}</div>
                <div className="text-[11px] text-gray-700 mt-0.5">
                  <span className="font-bold">{s.count}</span> cards
                </div>
                <div className="text-[11px] mt-0.5">
                  <span className="text-emerald-700 font-bold">Balance: {fmt(s.balance)}</span>
                  {' · '}
                  <span className="text-amber-700 font-bold">Used: {fmt(s.used)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Distribution Bar */}
      <div className="bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200 rounded p-2 flex items-center gap-2 flex-wrap" data-testid="distribute-bar">
        <span className="text-xs font-bold text-violet-800">{selected.size} selected</span>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={toggleAll}>Select All ({cards.filter(c => c.assigned_to_id === cards[0]?.assigned_to_id).length} mine)</Button>
        <span className="text-xs text-gray-600 ml-2">Distribute to:</span>
        <select
          className="text-xs border rounded px-2 py-1 bg-white"
          value={targetSubId}
          onChange={(e) => setTargetSubId(e.target.value)}
          data-testid="target-sub-staff-select"
        >
          <option value="">-- Select Sub-Staff --</option>
          {subStaff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <Button
          size="sm"
          className="bg-violet-600 hover:bg-violet-700 text-xs h-7 ml-auto"
          disabled={selected.size === 0 || !targetSubId || busy}
          onClick={distribute}
          data-testid="distribute-btn"
        >
          Distribute ({selected.size})
        </Button>
      </div>

      {/* Cards Table */}
      <div className="overflow-x-auto bg-white border rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
            <tr>
              <th className="px-2 py-2 w-8 text-center">
                <input type="checkbox" checked={selected.size > 0 && selected.size === cards.length} onChange={toggleAll} />
              </th>
              <th className="px-2 py-2 text-left">#</th>
              <th className="px-2 py-2 text-left">Card</th>
              <th className="px-2 py-2 text-left">Holder</th>
              <th className="px-2 py-2 text-right">Balance / Limit</th>
              <th className="px-2 py-2 text-right">Used</th>
              <th className="px-2 py-2 text-center">Daily</th>
              <th className="px-2 py-2 text-left">Assigned To</th>
              <th className="px-2 py-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center text-gray-400 py-8">Loading…</td></tr>
            ) : cards.length === 0 ? (
              <tr><td colSpan={9} className="text-center text-gray-400 py-8">Abhi koi card assign nahi hua hai. Admin se request karein.</td></tr>
            ) : cards.map((c, i) => {
              const isMine = c.assigned_to_id && subStaff.every(s => s.id !== c.assigned_to_id);
              const subOwner = subStaff.find(s => s.id === c.assigned_to_id);
              return (
                <tr key={c.id} className="border-t hover:bg-gray-50" data-testid={`my-card-row-${c.id}`}>
                  <td className="px-2 py-1 text-center">
                    {isMine && <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />}
                  </td>
                  <td className="px-2 py-1 text-gray-500">{i + 1}</td>
                  <td className="px-2 py-1 font-mono text-xs">
                    **** **** **** {c.last4}
                    {c.linked_agency_name && (
                      <div className="text-[9px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-1 inline-block mt-0.5" title={`Linked to ${c.linked_agency_name}`}>
                        🔒 {c.linked_agency_name}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1 text-xs">{c.card_holder || '—'}</td>
                  <td className="px-2 py-1 text-right text-xs whitespace-nowrap">
                    <span className={`font-bold ${Number(c.available_limit || 0) > 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(c.available_limit)}</span>
                    {c.initial_limit > 0 && c.initial_limit !== c.available_limit && (
                      <div className="text-[9px] text-gray-500">of {fmt(c.initial_limit)}</div>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right text-xs text-amber-700 font-bold">{fmt(c.used_amount)}</td>
                  <td className="px-2 py-1 text-center text-xs">{c.daily_usage_count}/{c.daily_limit}</td>
                  <td className="px-2 py-1 text-xs">
                    {isMine ? (
                      <span className="text-violet-700 font-bold">You (main)</span>
                    ) : subOwner ? (
                      <span className="text-blue-700">{subOwner.name}</span>
                    ) : '—'}
                  </td>
                  <td className="px-2 py-1 text-center">
                    {subOwner && (
                      <button onClick={() => pullback(c.id, subOwner.id)} className="text-[11px] text-amber-700 hover:underline" title="Pull back from sub-staff">⤺ Pull back</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-gray-500">
        💡 Tip: Jin cards pe aap select kar sakte ho wo aapke apne (main staff) cards hain. Sub-staff ke cards pull back kar ke wapas le sakte ho.
      </div>
    </div>
  );
}
